import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
// Import Shared Components
import { PasswordRequirements, Input, IconButton, Alert } from 'shared-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared Validations
import { verifyRegisterValidations } from 'shared-components/src/validations/cardholderValidations';
// Import Global Components
import InfoTooltip from '../../../../components/InfoTooltip';
import Title from '../../../../components/Title';
import SubmitButton from '../../../../components/SubmitButton';
// Import Global Layouts
import InputGroup from '../../../../layouts/InputGroup';
import InputRow from '../../../../layouts/InputRow';
import Form from '../../../../layouts/Form';
// Import Utils
import { changePassword } from '../../../../utils/auth';
import { verifyLoginCreds } from '../../../../utils/profile';
import { errorMessage } from '../../../../utils/validationHandlers';
import { FieldError } from '../../../Auth/styles';
import { TitleWrapper } from '../Form/styles';

// Both endpoints answer these with generic codes (error_occured, invalid_parameters),
// so the message is the only thing that tells them apart. Each maps to the field it
// concerns, as registration does; anything unmapped is shown as the server sent it.
const apiFieldErrors = {
  'Incorrect current password, please try again': {
    field: 'currentPassword',
    key: 'errors-incorrect-current-password',
  },
  'Please create a password that has not been used.': {
    field: 'password',
    key: 'errors-password-previously-used',
  },
};

// Same messages and order of checks as registration. The current password is only
// required: it is checked against the server, not today's rules, so a password that
// predates them can still be changed.
const fieldError = (field, info) => {
  switch (field) {
  case 'currentPassword':
    return info.currentPassword ? '' : 'errors-current-password-required';
  case 'password':
    if (!info.password) return 'errors-password-required';
    return verifyRegisterValidations.password(info.password)
      ? ''
      : 'errors-password-invalid';
  case 'passwordConfirmation':
    if (!info.passwordConfirmation)
      return 'errors-password-confirmation-required';
    return info.password === info.passwordConfirmation
      ? ''
      : 'errors-password-confirmation-invalid';
  default:
    return '';
  }
};

const passwordFields = ['currentPassword', 'password', 'passwordConfirmation'];

// Mirrors the checklist shown under the password on registration.
const passwordRules = (password = '') => ({
  minLength: password.length >= 12,
  hasUppercase: /[A-Z]/.test(password),
  hasNumber: /\d/.test(password),
  hasSymbol: /[!@#$*]/.test(password),
});

const LoginCredentialsForm = ({ username, iconType, onClick }) => {
  // Set States
  const initialRegisterInfo = {
    username: '',
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  };
  const initialValidationErrors = {
    username: '',
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  };
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // create intl instance
  const intl = useIntl();
  
  // Create reducer with initial card information state
  // Returns the updated state to be passed to components and a function to update the state
  const [resetPasswordInfo, setResetPasswordInfo] = useUpdateStateObject(
    initialRegisterInfo
  );
  const [validationErrors, setValidationErrors] = useUpdateStateObject(
    initialValidationErrors
  );

  const [showPasswords, setShowPasswords] = useState(false);

  const passwordInputType = showPasswords ? 'text' : 'password';
      
  const handleChangePassword = async payload => {
    setSubmitting(true);
    try {
      const verifyPayload = {
        username,
        password: resetPasswordInfo.currentPassword,
      };
    
      await verifyLoginCreds(verifyPayload);
      setFormError(null);
      await changePassword(payload);
      setResetPasswordInfo({ key: 'username', value: '' });
      setResetPasswordInfo({ key: 'currentPassword', value: '' });
      setResetPasswordInfo({ key: 'password', value: '' });
      setResetPasswordInfo({ key: 'passwordConfirmation', value: '' });
      setValidationErrors({ key: 'username', value: '' });
      setValidationErrors({ key: 'currentPassword', value: '' });
      setValidationErrors({ key: 'password', value: '' });
      setValidationErrors({ key: 'passwordConfirmation', value: '' });
      setShowPasswords(false);
      setSuccess('password-reset-success-alert');
    } catch (e) {
      const mapped = apiFieldErrors[e.message];
      if (mapped) {
        setValidationErrors({ key: mapped.field, value: mapped.key });
      } else {
        setFormError(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validateField = field =>
    setValidationErrors({ key: field, value: fieldError(field, resetPasswordInfo) });

  // Set Handlers
  const handleSubmit = () => {
    // The button is disabled meanwhile; this also covers Enter in a field.
    if (submitting) return;
    setFormError(null);

    // Every field gets its message at once, rather than one banner at a time
    const errors = passwordFields.map(field => [
      field,
      fieldError(field, resetPasswordInfo),
    ]);
    errors.forEach(([key, value]) => setValidationErrors({ key, value }));
    if (errors.some(([, value]) => value)) return;

    // Create payload to be sent
    const payload = {
      username,
      password: resetPasswordInfo.password,
      password_confirmation: resetPasswordInfo.passwordConfirmation,
    };

    handleChangePassword(payload);
  };

  const page = 'profile';
  const rules = passwordRules(resetPasswordInfo.password);

  return (
    <Form as="div">
      <TitleWrapper>
        <Title
          title={intl.messages['profile-login-credentials']}
          titleFor="login-credentials"
          page="profile"
        />
        <IconButton
          iconType={iconType}
          onClick={()=> {
            setFormError(null);
            onClick();
          }}
        />
      </TitleWrapper>
      {iconType === 'edit' ?
        (
          <InputRow>
            <InputGroup
              label={intl.messages[`${page}-username-label`]}
              labelFor="username"
              page={page}
              required
            >
              <Input
                value={username}
                id="profile-username"
                autocomplete="username"
                disabled="disabled"
                maxLength={30}
                required
              />
            </InputGroup>
            <InputGroup
              label={intl.messages[`${page}-password-label`]}
              labelFor="password"
              page={page}
            >
              <Input
                value='********'
                id="profile-password"
                autocomplete="password"
                disabled
              />
            </InputGroup>
          </InputRow>
        ) : (
          <>
            {/* noValidate: the browser's own messages ("Please match the requested
                format.") follow the browser's language, not the portal's. The
                checks in handleSubmit cover the same ground in translated text. */}
            <form
              noValidate
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <InputRow>
                <InputGroup
                  label={intl.messages['auth-current-password-label']}
                  labelFor="current-password"
                  page={page}
                  required
                >
                  <Input
                    value={resetPasswordInfo.currentPassword}
                    error={validationErrors.currentPassword}
                    onBlur={() => validateField('currentPassword')}
                    onChange={e =>
                      setResetPasswordInfo({
                        key: 'currentPassword',
                        value: e.target.value.trim(),
                      })}
                    type={passwordInputType}
                    required
                    placeholder={intl.messages['auth-current-password-placeholder']}
                  />
                  {validationErrors.currentPassword && (
                    <FieldError className="invalid-feedback">
                      {errorMessage(intl, validationErrors.currentPassword)}
                    </FieldError>
                  )}
                </InputGroup>
                <InputGroup
                  tooltip={(
                    <InfoTooltip
                      position="top"
                      content={<PasswordRequirements messages={intl.messages} />}
                    />
                  )}
                  label={intl.messages['auth-new-password-label']}
                  labelFor="new-password"
                  page={page}
                  required
                >
                  <Input
                    value={resetPasswordInfo.password}
                    error={validationErrors.password}
                    onBlur={() => validateField('password')}
                    onChange={e =>
                      setResetPasswordInfo({
                        key: 'password',
                        value: e.target.value.trim(),
                      })}
                    type={passwordInputType}
                    minLength={12}
                    required
                    placeholder={intl.messages['auth-new-password-placeholder']}
                  />
                  {validationErrors.password && (
                    <FieldError className="invalid-feedback">
                      {errorMessage(intl, validationErrors.password)}
                      {validationErrors.password === 'errors-password-invalid' && (
                        <div>
                          <p className={rules.minLength ? 'valid' : 'invalid'}>
                            • {intl.messages['errors-password-length']}
                          </p>
                          <p className={rules.hasUppercase ? 'valid' : 'invalid'}>
                            • {intl.messages['errors-password-case']}
                          </p>
                          <p className={rules.hasNumber ? 'valid' : 'invalid'}>
                            • {intl.messages['errors-password-number']}
                          </p>
                          <p className={rules.hasSymbol ? 'valid' : 'invalid'}>
                            • {intl.messages['errors-password-symbol']}
                          </p>
                        </div>
                      )}
                    </FieldError>
                  )}
                </InputGroup>
                <InputGroup
                  label={intl.messages['auth-password-confirmation-label']}
                  labelFor='password-confirmation'
                  page={page}
                  required
                >
                  <Input
                    value={resetPasswordInfo.passwordConfirmation}
                    error={validationErrors.passwordConfirmation}
                    onBlur={() => validateField('passwordConfirmation')}
                    onChange={e =>
                      setResetPasswordInfo({
                        key: 'passwordConfirmation',
                        value: e.target.value.trim(),
                      })}
                    type={passwordInputType}
                    minLength={12}
                    required
                    placeholder={intl.messages['auth-password-confirmation-placeholder']}
                  />
                  {validationErrors.passwordConfirmation && (
                    <FieldError className="invalid-feedback">
                      {errorMessage(intl, validationErrors.passwordConfirmation)}
                    </FieldError>
                  )}
                </InputGroup>
              </InputRow>
              <div>
                <label htmlFor='show-passwords' style={{position: 'relative', margin: '15px 0', display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                  <input
                    id='show-passwords'
                    type='checkbox'
                    name='show-passwords'
                    checked={showPasswords}
                    onChange={()=>setShowPasswords(!showPasswords)}
                    style={{marginRight:'5px'}}
                  />
                  <span>{intl.messages[`${page}-show-passwords-label`]}</span>
                </label>
              </div>
              <SubmitButton loading={submitting} />
            </form>
            {formError &&  (
              <Alert type="danger">
                <FormattedMessage
                  id={formError}
                  description="Alert error"
                  defaultMessage={errorMessage(intl, formError)}
                />
              </Alert>
            )}
            {success && !formError && (
              <Alert type="success">
                <FormattedMessage
                  id={success}
                  description="Alert success"
                  defaultMessage={intl.messages[success]}
                />
              </Alert>
            )}
          </>
        )}
    </Form>
  );
};

LoginCredentialsForm.propTypes = {
  username: PropTypes.string.isRequired,
  onClick:  PropTypes.func.isRequired,
  iconType: PropTypes.string.isRequired,
};

export default LoginCredentialsForm;
